import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { RunInstance, RunResult, RunStatus, WorkflowStep, Workflow, SessionMessage, SessionInfo } from '../types';
import apiService from '../services/api';

// Enhanced types for async workflow management
export interface AsyncOperation {
  id: string;
  type: 'start' | 'pause' | 'resume' | 'stop' | 'validate' | 'export' | 'import';
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: number;
  error?: string;
  retryCount: number;
  maxRetries: number;
  startTime: number;
  endTime?: number;
}

export interface WorkflowExecution {
  runInstance: RunInstance | null;
  runResults: RunResult[];
  currentStep: number;
  totalSteps: number;
  status: RunStatus;
  progress: number;
  isRunning: boolean;
  isPaused: boolean;
  error: string | null;
  lastUpdated: number;
  estimatedTimeRemaining?: number;
  stepStatuses: Map<string, RunStatus>;
  stepProgress: Map<string, number>;
  stepErrors: Map<string, string>;
  isPolling: boolean;
  pollingStartTime: number | null;
  lastPollTime: number | null;
  pollErrorCount: number;
  waitingForUserInput: boolean;
  userInputRunResultId: string | null;
  userInputMessage: string | null;
  sessionMessages: SessionMessage[];
  sessionInfoMap: Record<number, SessionInfo>;
}

export interface RealTimeConnection {
  websocket: WebSocket | null;
  isConnected: boolean;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectInterval: number;
  lastHeartbeat: number;
  subscriptions: Set<string>;
}

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryableErrors: string[];
}

export interface OptimisticUpdate {
  id: string;
  type: 'step_start' | 'step_complete' | 'status_change';
  timestamp: number;
  data: any;
  confirmed: boolean;
  rollbackData?: any;
}

interface WorkflowStore {
  // Execution state
  executions: Map<string, WorkflowExecution>;
  activeOperations: Map<string, AsyncOperation>;
  optimisticUpdates: Map<string, OptimisticUpdate>;
  
  // Real-time connection
  realTimeConnection: RealTimeConnection;
  
  // Configuration
  retryConfig: RetryConfig;
  pollingInterval: number;
  enableOptimisticUpdates: boolean;
  
  // Loading states
  isLoading: boolean;
  loadingOperations: Set<string>;
  
  // Error handling
  globalError: string | null;
  operationErrors: Map<string, string>;
  
  // Actions - Execution Control
  startWorkflow: (workflowId: string) => Promise<void>;
  pauseWorkflow: (workflowId: string) => Promise<void>;
  resumeWorkflow: (workflowId: string) => Promise<void>;
  stopWorkflow: (workflowId: string) => Promise<void>;
  
  // Actions - Data Management
  loadExecution: (workflowId: string) => Promise<void>;
  refreshExecution: (workflowId: string) => Promise<void>;
  clearExecution: (workflowId: string) => void;
  clearSessionData: (workflowId: string, options?: {
    stopWorkflow?: boolean;
    clearMode?: 'soft' | 'hard';
    reason?: string;
  }) => Promise<void>;
  
  // Actions - Real-time Updates
  connectRealTime: (workflowId: string) => Promise<void>;
  disconnectRealTime: (workflowId: string) => void;
  handleRealTimeMessage: (message: any) => void;
  
  // Actions - Enhanced Polling
  startPolling: (workflowId: string, runInstanceId: string) => Promise<void>;
  stopPolling: (workflowId: string) => void;
  updateRunResults: (workflowId: string, results: RunResult[]) => void;
  addRunResult: (workflowId: string, result: RunResult) => void;
  setUserInputRequired: (workflowId: string, runResultId: string, message: string) => void;
  clearUserInputRequired: (workflowId: string) => void;
  
  // Actions - Optimistic Updates
  addOptimisticUpdate: (update: OptimisticUpdate) => void;
  confirmOptimisticUpdate: (updateId: string) => void;
  rollbackOptimisticUpdate: (updateId: string) => void;
  
  // Actions - Error Handling
  setError: (error: string | null) => void;
  clearError: () => void;
  retryOperation: (projectId: string) => Promise<void>;
  
  // Actions - Async Operations
  trackOperation: (project: AsyncOperation) => void;
  updateProject: (projectId: string, updates: Partial<AsyncOperation>) => void;
  completeOperation: (projectId: string, success: boolean, error?: string) => void;
  
  // Utilities
  getExecution: (workflowId: string) => WorkflowExecution | null;
  isOperationRunning: (type: AsyncOperation['type'], workflowId?: string) => boolean;
  cleanup: () => void;
  
  // Enhanced Polling
  pollers: Map<string, { stop: () => void }>;
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  retryableErrors: ['NetworkError', 'TimeoutError', 'ServerError'],
};

export const defaultExecution = (): WorkflowExecution => ({
  runInstance: null,
  runResults: [],
  currentStep: 0,
  totalSteps: 0,
  status: 'pending',
  progress: 0,
  isRunning: false,
  isPaused: false,
  error: null,
  lastUpdated: Date.now(),
  stepStatuses: new Map(),
  stepProgress: new Map(),
  stepErrors: new Map(),
  isPolling: false,
  pollingStartTime: null,
  lastPollTime: null,
  pollErrorCount: 0,
  waitingForUserInput: false,
  userInputRunResultId: null,
  userInputMessage: null,
  sessionMessages: [],
  sessionInfoMap: {},
});

const defaultRealTimeConnection = (): RealTimeConnection => ({
  websocket: null,
  isConnected: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectInterval: 5000,
  lastHeartbeat: 0,
  subscriptions: new Set(),
});

export const useWorkflowStore = create<WorkflowStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    executions: new Map(),
    activeOperations: new Map(),
    optimisticUpdates: new Map(),
    realTimeConnection: defaultRealTimeConnection(),
    retryConfig: defaultRetryConfig,
    pollingInterval: 2000,
    enableOptimisticUpdates: true,
    isLoading: false,
    loadingOperations: new Set(),
    globalError: null,
    operationErrors: new Map(),
    pollers: new Map(),

    // Execution Control Actions
    startWorkflow: async (workflowId: string) => {
      const projectId = `start-${workflowId}-${Date.now()}`;
      const project: AsyncOperation = {
        id: projectId,
        type: 'start',
        status: 'pending',
        retryCount: 0,
        maxRetries: get().retryConfig.maxRetries,
        startTime: Date.now(),
      };

      get().trackOperation(project);

      try {
        // Optimistic update
        if (get().enableOptimisticUpdates) {
          const optimisticUpdate: OptimisticUpdate = {
            id: `start-optimistic-${Date.now()}`,
            type: 'status_change',
            timestamp: Date.now(),
            data: { status: 'running', isRunning: true },
            confirmed: false,
          };
          get().addOptimisticUpdate(optimisticUpdate);
        }

        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.add(projectId);
          return { loadingOperations: newLoadingOperations };
        });

        get().updateProject(projectId, { status: 'running' });

        const runInstance = await apiService.startWorkflowRun(workflowId);
        window.dispatchEvent(new CustomEvent('agent-salute'));

        // Update execution state
        const executions = new Map(get().executions);
        const execution = executions.get(workflowId) || defaultExecution();
        execution.runInstance = runInstance;
        execution.status = 'running';
        execution.isRunning = true;
        execution.isPaused = false;
        execution.lastUpdated = Date.now();
        executions.set(workflowId, execution);

        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.delete(projectId);
          return { executions, loadingOperations: newLoadingOperations };
        });

        get().completeOperation(projectId, true);

        // Connect to real-time updates and start polling
        await get().connectRealTime(workflowId);
        await get().startPolling(workflowId, runInstance.run_instance_id.toString());

      } catch (error: any) {
        console.error('Failed to start workflow:', error);
        
        // Rollback optimistic update
        const optimisticUpdates = new Map(get().optimisticUpdates);
        optimisticUpdates.forEach((update, id) => {
          if (update.type === 'status_change' && !update.confirmed) {
            get().rollbackOptimisticUpdate(id);
          }
        });

        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.delete(projectId);
          return { loadingOperations: newLoadingOperations };
        });

        get().completeOperation(projectId, false, error.message);
        
        // Retry logic
        if (project.retryCount < project.maxRetries && 
            get().retryConfig.retryableErrors.some(err => error.message.includes(err))) {
          setTimeout(() => get().retryOperation(projectId), 
            Math.min(get().retryConfig.baseDelay * Math.pow(get().retryConfig.backoffMultiplier, project.retryCount), 
                    get().retryConfig.maxDelay));
        }
      }
    },

    pauseWorkflow: async (workflowId: string) => {
      const execution = get().executions.get(workflowId);
      if (!execution?.runInstance) return;

      const projectId = `pause-${workflowId}-${Date.now()}`;
      const project: AsyncOperation = {
        id: projectId,
        type: 'pause',
        status: 'pending',
        retryCount: 0,
        maxRetries: get().retryConfig.maxRetries,
        startTime: Date.now(),
      };

      get().trackOperation(project);

      try {
        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.add(projectId);
          return { loadingOperations: newLoadingOperations };
        });

        await apiService.pauseWorkflowRun(execution.runInstance.run_instance_id);
        
        const executions = new Map(get().executions);
        const updatedExecution = { ...execution };
        updatedExecution.status = 'paused';
        updatedExecution.isRunning = false;
        updatedExecution.isPaused = true;
        updatedExecution.lastUpdated = Date.now();
        executions.set(workflowId, updatedExecution);

        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.delete(projectId);
          return { executions, loadingOperations: newLoadingOperations };
        });

        get().completeOperation(projectId, true);

      } catch (error: any) {
        console.error('Failed to pause workflow:', error);
        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.delete(projectId);
          return { loadingOperations: newLoadingOperations };
        });
        get().completeOperation(projectId, false, error.message);
      }
    },

    resumeWorkflow: async (workflowId: string) => {
      const execution = get().executions.get(workflowId);
      if (!execution?.runInstance) return;

      const projectId = `resume-${workflowId}-${Date.now()}`;
      const project: AsyncOperation = {
        id: projectId,
        type: 'resume',
        status: 'pending',
        retryCount: 0,
        maxRetries: get().retryConfig.maxRetries,
        startTime: Date.now(),
      };

      get().trackOperation(project);

      try {
        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.add(projectId);
          return { loadingOperations: newLoadingOperations };
        });

        await apiService.resumeWorkflowRun(execution.runInstance.run_instance_id);
        
        const executions = new Map(get().executions);
        const updatedExecution = { ...execution };
        updatedExecution.status = 'running';
        updatedExecution.isRunning = true;
        updatedExecution.isPaused = false;
        updatedExecution.lastUpdated = Date.now();
        executions.set(workflowId, updatedExecution);

        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.delete(projectId);
          return { executions, loadingOperations: newLoadingOperations };
        });

        get().completeOperation(projectId, true);

        // Start polling after resume
        await get().startPolling(workflowId, execution.runInstance.run_instance_id.toString());

      } catch (error: any) {
        console.error('Failed to resume workflow:', error);
        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.delete(projectId);
          return { loadingOperations: newLoadingOperations };
        });
        get().completeOperation(projectId, false, error.message);
      }
    },

    stopWorkflow: async (workflowId: string) => {
      const execution = get().executions.get(workflowId);
      if (!execution?.runInstance) return;

      const projectId = `stop-${workflowId}-${Date.now()}`;
      const project: AsyncOperation = {
        id: projectId,
        type: 'stop',
        status: 'pending',
        retryCount: 0,
        maxRetries: get().retryConfig.maxRetries,
        startTime: Date.now(),
      };

      get().trackOperation(project);

      try {
        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.add(projectId);
          return { loadingOperations: newLoadingOperations };
        });

        await apiService.stopWorkflowRun(execution.runInstance.run_instance_id);
        
        const executions = new Map(get().executions);
        const updatedExecution = { ...execution };
        updatedExecution.status = 'stopped';
        updatedExecution.isRunning = false;
        updatedExecution.isPaused = false;
        updatedExecution.lastUpdated = Date.now();
        executions.set(workflowId, updatedExecution);

        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.delete(projectId);
          return { executions, loadingOperations: newLoadingOperations };
        });

        get().completeOperation(projectId, true);
        get().stopPolling(workflowId);  // Stop polling when workflow is stopped
        get().disconnectRealTime(workflowId);

      } catch (error: any) {
        console.error('Failed to stop workflow:', error);
        set(state => {
          const newLoadingOperations = new Set(state.loadingOperations);
          newLoadingOperations.delete(projectId);
          return { loadingOperations: newLoadingOperations };
        });
        get().completeOperation(projectId, false, error.message);
      }
    },

    // Data Management Actions
    loadExecution: async (workflowId: string) => {
      // Don't overwrite an active/recent execution with historical data
      const currentExec = get().executions.get(workflowId);
      const isActive = currentExec?.isRunning || currentExec?.isPolling ||
        currentExec?.status === 'running' || currentExec?.status === 'pending';
      // Also skip if the execution completed very recently (within 5s) to avoid HMR/remount races
      const isRecent = currentExec?.lastUpdated && (Date.now() - currentExec.lastUpdated < 5000);
      // Detect orphaned-active state: cached status says active but no poller is running.
      // This happens when an HMR or remount kills polling mid-run; in that case we MUST
      // continue with the fresh load so polling can be re-attached, otherwise the UI is
      // stuck at the last polled snapshot until the user hard-refreshes.
      const hasActivePoller = !!get().pollers.get(workflowId);
      const orphanedActive = isActive && !hasActivePoller;
      if (!orphanedActive && (isActive || (isRecent && currentExec?.runResults && currentExec.runResults.length > 0))) {
        console.log('[DEBUG] loadExecution: Skipping — execution is active or recent (status=' + currentExec?.status + ')');
        return;
      }
      if (orphanedActive) {
        console.log('[DEBUG] loadExecution: Detected orphaned-active state (no poller). Reloading and resuming polling.');
      }

      set({ isLoading: true });

      try {
        const [runInstances, orchestration] = await Promise.all([
          apiService.getRunInstances(workflowId),
          apiService.getWorkflow(workflowId)
        ]);

        const latestRun = runInstances[0]; // Assuming sorted by most recent
        let runResults: RunResult[] = [];
        let sessionMessages: SessionMessage[] = [];
        let sessionInfoMap: Record<number, SessionInfo> = {};

        if (latestRun) {
          const response = await apiService.getRunInstanceResults(latestRun.run_instance_id, true);
          runResults = response.results;
          sessionMessages = response.session_messages || [];
          sessionInfoMap = response.session_info_map || {};
          console.log(`[DEBUG] loadExecution: Loaded ${runResults.length} results, ${sessionMessages.length} session messages, ${Object.keys(sessionInfoMap).length} session info entries`);
        }

        const execution: WorkflowExecution = {
          runInstance: latestRun || null,
          runResults,
          currentStep: runResults.filter(r => r.status === 'completed').length,
          totalSteps: runResults.length,
          status: latestRun?.status || 'pending',
          progress: runResults.length > 0 ?
            (runResults.filter(r => r.status === 'completed').length / runResults.length) * 100 : 0,
          isRunning: latestRun?.status === 'running' || false,
          isPaused: latestRun?.status === 'paused' || false,
          error: null,
          lastUpdated: Date.now(),
          stepStatuses: new Map(runResults.map(r => [r.orchestration_step_id, r.status])),
          stepProgress: new Map(),
          stepErrors: new Map(runResults.filter(r => r.error_message).map(r => [r.orchestration_step_id, r.error_message!])),
          isPolling: false,
          pollingStartTime: null,
          lastPollTime: null,
          pollErrorCount: 0,
          waitingForUserInput: false,
          userInputRunResultId: null,
          userInputMessage: null,
          sessionMessages,
          sessionInfoMap,
        };

        const executions = new Map(get().executions);
        executions.set(workflowId, execution);

        set({ executions, isLoading: false });

        // Auto-resume polling whenever the latest run is in an active state and
        // no poller is currently attached to this workflow. Covers paused
        // workflows (waiting for user input), running workflows after an HMR
        // or remount that killed polling mid-run, and pending starts.
        const isActiveStatus =
          execution.runInstance &&
          (execution.status === 'running' ||
            execution.status === 'pending' ||
            execution.status === 'paused');
        const pollerAttached = !!get().pollers.get(workflowId);
        const shouldResumePolling = isActiveStatus && !pollerAttached;

        if (shouldResumePolling && execution.runInstance) {
          console.log(`[DEBUG] loadExecution: Resuming polling for runInstance: ${execution.runInstance.run_instance_id} (status=${execution.status})`);
          // Note: Polling works without WebSocket - WebSocket is optional for real-time updates
          await get().startPolling(workflowId, execution.runInstance.run_instance_id.toString());
        } else {
          console.log(`[DEBUG] loadExecution: Loaded execution with status '${execution.status}', isRunning=${execution.isRunning}, isPaused=${execution.isPaused}, shouldResumePolling=${shouldResumePolling}, pollerAttached=${pollerAttached}`);
        }

      } catch (error: any) {
        console.error('Failed to load execution:', error);
        set({ isLoading: false, globalError: error.message });
      }
    },

    refreshExecution: async (workflowId: string) => {
      await get().loadExecution(workflowId);
    },

    clearExecution: (workflowId: string) => {
      console.log('[DEBUG] clearExecution called for workflowId:', workflowId);
      // Stop polling before clearing execution
      get().stopPolling(workflowId);
      const executions = new Map(get().executions);
      executions.delete(workflowId);
      set({ executions });
      get().disconnectRealTime(workflowId);
      console.log('[DEBUG] clearExecution: Execution cleared for workflowId:', workflowId);
    },

    clearSessionData: async (workflowId: string, options = {}) => {
      const execution = get().executions.get(workflowId);
      if (!execution?.runInstance) {
        console.error('[DEBUG] clearSessionData: No execution or run instance found for workflowId:', workflowId);
        return;
      }

      const {
        stopWorkflow = false,
        clearMode = 'soft',
        reason = 'User requested session clear'
      } = options;

      try {
        set({ isLoading: true });

        // Call the API to clear sessions
        const response = await apiService.clearRunInstanceSessions(
          workflowId,
          execution.runInstance.run_instance_id,
          {
            confirm: true,
            clear_mode: clearMode,
            reason,
            stop_orchestration: stopWorkflow
          }
        );

        console.log('[DEBUG] clearSessionData: Response:', response);

        // Update local state - clear session data
        const executions = new Map(get().executions);
        const updatedExecution = { ...execution };
        updatedExecution.sessionMessages = [];
        updatedExecution.sessionInfoMap = {};
        updatedExecution.lastUpdated = Date.now();

        // If workflow was stopped, update status
        if (response.orchestration_stopped) {
          updatedExecution.status = 'stopped';
          updatedExecution.isRunning = false;
          updatedExecution.isPaused = false;
          get().stopPolling(workflowId);
        }

        executions.set(workflowId, updatedExecution);
        set({ executions, isLoading: false });

        console.log(`[DEBUG] clearSessionData: Successfully cleared ${response.messages_deleted} messages and ${response.sessions_marked_inactive} sessions`);

        // CRITICAL FIX: Clear localStorage FIRST, before updating Zustand state
        // This prevents race conditions where persist middleware rehydrates old data on page reload
        // The issue: Zustand persist rehydrates from localStorage on page load BEFORE components mount,
        // so we must update localStorage synchronously and immediately
        try {
          const storageKey = 'assistant-chat-storage';
          const currentStorage = localStorage.getItem(storageKey);

          if (currentStorage) {
            const parsedStorage = JSON.parse(currentStorage);
            const stateToSave = parsedStorage.state || {};

            // Find all session keys for this workflow
            const sessionKeysToDelete: string[] = [];
            if (stateToSave.sessionMetadata) {
              for (const [sessionKey, metadata] of Object.entries(stateToSave.sessionMetadata as Record<string, any>)) {
                if (metadata && metadata.workflowId === parseInt(workflowId)) {
                  sessionKeysToDelete.push(sessionKey);
                }
              }
            }

            console.log(`[DEBUG] clearSessionData: Found ${sessionKeysToDelete.length} session keys to clear in localStorage for workflowId ${workflowId}`);

            // Clear messages and reset metadata for all matching sessions
            for (const sessionKey of sessionKeysToDelete) {
              if (stateToSave.messagesBySession && stateToSave.messagesBySession[sessionKey]) {
                stateToSave.messagesBySession[sessionKey] = [];
                console.log(`[DEBUG] clearSessionData: Cleared messages for session key: ${sessionKey}`);
              }
              if (stateToSave.sessionMetadata && stateToSave.sessionMetadata[sessionKey]) {
                stateToSave.sessionMetadata[sessionKey] = {
                  ...stateToSave.sessionMetadata[sessionKey],
                  turnCount: 0,
                  lastMessageAt: undefined
                };
                console.log(`[DEBUG] clearSessionData: Reset metadata for session key: ${sessionKey}`);
              }
            }

            // Save back to localStorage IMMEDIATELY
            parsedStorage.state = stateToSave;
            localStorage.setItem(storageKey, JSON.stringify(parsedStorage));
            console.log('[DEBUG] clearSessionData: Successfully updated localStorage with cleared state');

            // Now update Zustand state to match (for UI reactivity)
            const { useAssistantChatStore } = await import('./assistantChatStore');
            useAssistantChatStore.setState((state) => {
              const updatedMessagesBySession = { ...state.messagesBySession };
              const updatedSessionMetadata = { ...state.sessionMetadata };

              for (const sessionKey of sessionKeysToDelete) {
                updatedMessagesBySession[sessionKey] = [];
                const metadata = updatedSessionMetadata[sessionKey];
                if (metadata) {
                  updatedSessionMetadata[sessionKey] = {
                    ...metadata,
                    turnCount: 0,
                    lastMessageAt: undefined
                  };
                }
              }

              return {
                messagesBySession: updatedMessagesBySession,
                sessionMetadata: updatedSessionMetadata
              };
            });

            console.log('[DEBUG] clearSessionData: Successfully updated Zustand state to match localStorage');
          }
        } catch (error: any) {
          console.error('[DEBUG] clearSessionData: Failed to clear localStorage and assistant chat store:', error);
          // Don't fail the whole project
        }

      } catch (error: any) {
        console.error('[DEBUG] clearSessionData: Failed to clear session data:', error);
        set({ isLoading: false, globalError: error.message });
        throw error;
      }
    },

    // Real-time Connection Actions
    connectRealTime: async (workflowId: string) => {
      const { realTimeConnection } = get();
      
      if (realTimeConnection.isConnected && realTimeConnection.subscriptions.has(workflowId)) {
        return; // Already connected
      }

      try {
        const wsUrl = `${import.meta.env.VITE_WS_URL || 'ws://localhost:8000'}/ws/orchestration/${workflowId}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          const subscriptions = new Set(realTimeConnection.subscriptions);
          subscriptions.add(workflowId);
          
          set({
            realTimeConnection: {
              ...realTimeConnection,
              websocket: ws,
              isConnected: true,
              reconnectAttempts: 0,
              lastHeartbeat: Date.now(),
              subscriptions,
            }
          });
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            get().handleRealTimeMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        ws.onclose = () => {
          set({
            realTimeConnection: {
              ...get().realTimeConnection,
              isConnected: false,
            }
          });

          // Auto-reconnect logic
          const connection = get().realTimeConnection;
          if (connection.reconnectAttempts < connection.maxReconnectAttempts) {
            setTimeout(() => {
              set({
                realTimeConnection: {
                  ...get().realTimeConnection,
                  reconnectAttempts: connection.reconnectAttempts + 1,
                }
              });
              get().connectRealTime(workflowId);
            }, connection.reconnectInterval);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };

      } catch (error: any) {
        console.error('Failed to connect to real-time updates:', error);
      }
    },

    disconnectRealTime: (workflowId: string) => {
      const { realTimeConnection } = get();
      const subscriptions = new Set(realTimeConnection.subscriptions);
      subscriptions.delete(workflowId);

      if (subscriptions.size === 0 && realTimeConnection.websocket) {
        realTimeConnection.websocket.close();
        set({
          realTimeConnection: {
            ...realTimeConnection,
            websocket: null,
            isConnected: false,
            subscriptions,
          }
        });
      } else {
        set({
          realTimeConnection: {
            ...realTimeConnection,
            subscriptions,
          }
        });
      }
    },

    handleRealTimeMessage: (message: any) => {
      const { type, payload, workflowId } = message;
      
      switch (type) {
        case 'run_status_update':
          const executions = new Map(get().executions);
          const execution = executions.get(workflowId);
          if (execution) {
            const updatedExecution = {
              ...execution,
              status: payload.status,
              isRunning: payload.status === 'running',
              isPaused: payload.status === 'paused',
              currentStep: payload.currentStep || execution.currentStep,
              progress: payload.progress || execution.progress,
              lastUpdated: Date.now(),
            };
            executions.set(workflowId, updatedExecution);
            set({ executions });
          }
          break;

        case 'step_completed':
          const stepExecutions = new Map(get().executions);
          const stepExecution = stepExecutions.get(workflowId);
          if (stepExecution) {
            stepExecution.stepStatuses.set(payload.stepId, 'completed');
            stepExecution.stepProgress.set(payload.stepId, 100);
            stepExecution.currentStep = payload.stepOrder;
            stepExecution.lastUpdated = Date.now();
            stepExecutions.set(workflowId, stepExecution);
            set({ executions: stepExecutions });
          }
          break;

        case 'step_failed':
          const failedExecutions = new Map(get().executions);
          const failedExecution = failedExecutions.get(workflowId);
          if (failedExecution) {
            failedExecution.stepStatuses.set(payload.stepId, 'failed');
            failedExecution.stepErrors.set(payload.stepId, payload.error);
            failedExecution.lastUpdated = Date.now();
            failedExecutions.set(workflowId, failedExecution);
            set({ executions: failedExecutions });
          }
          break;
      }
    },

    // Optimistic Updates
    addOptimisticUpdate: (update: OptimisticUpdate) => {
      const optimisticUpdates = new Map(get().optimisticUpdates);
      optimisticUpdates.set(update.id, update);
      set({ optimisticUpdates });
    },

    confirmOptimisticUpdate: (updateId: string) => {
      const optimisticUpdates = new Map(get().optimisticUpdates);
      const update = optimisticUpdates.get(updateId);
      if (update) {
        update.confirmed = true;
        optimisticUpdates.set(updateId, update);
        set({ optimisticUpdates });
      }
    },

    rollbackOptimisticUpdate: (updateId: string) => {
      const optimisticUpdates = new Map(get().optimisticUpdates);
      const update = optimisticUpdates.get(updateId);
      if (update && update.rollbackData) {
        // Apply rollback data to execution state
        // Implementation depends on the specific update type
        optimisticUpdates.delete(updateId);
        set({ optimisticUpdates });
      }
    },

    // Error Handling
    setError: (error: string | null) => {
      set({ globalError: error });
    },

    clearError: () => {
      set({ globalError: null });
    },

    retryOperation: async (projectId: string) => {
      const project = get().activeOperations.get(projectId);
      if (!project) return;

      project.retryCount++;
      get().updateProject(projectId, { 
        status: 'pending', 
        retryCount: project.retryCount 
      });

      // Re-execute the project based on its type
      // This would need to be implemented based on the specific project
    },

    // Project Tracking
    trackOperation: (project: AsyncOperation) => {
      const activeOperations = new Map(get().activeOperations);
      activeOperations.set(project.id, project);
      set({ activeOperations });
    },

    updateProject: (projectId: string, updates: Partial<AsyncOperation>) => {
      const activeOperations = new Map(get().activeOperations);
      const project = activeOperations.get(projectId);
      if (project) {
        const updatedOperation = { ...project, ...updates };
        activeOperations.set(projectId, updatedOperation);
        set({ activeOperations });
      }
    },

    completeOperation: (projectId: string, success: boolean, error?: string) => {
      const activeOperations = new Map(get().activeOperations);
      const project = activeOperations.get(projectId);
      if (project) {
        project.status = success ? 'completed' : 'failed';
        project.endTime = Date.now();
        if (error) project.error = error;
        activeOperations.set(projectId, project);
        set({ activeOperations });

        // Clean up completed projects after a delay
        setTimeout(() => {
          const currentProjects = new Map(get().activeOperations);
          currentProjects.delete(projectId);
          set({ activeOperations: currentProjects });
        }, 30000); // Keep for 30 seconds for UI feedback
      }
    },

    // Utilities
    getExecution: (workflowId: string) => {
      return get().executions.get(workflowId) || null;
    },

    isOperationRunning: (type: AsyncOperation['type'], workflowId?: string) => {
      const projects = get().activeOperations;
      const operationValues = Array.from(projects.values());
      for (let i = 0; i < operationValues.length; i++) {
        const project = operationValues[i];
        if (project.type === type && project.status === 'running') {
          if (!workflowId || project.id.includes(workflowId)) {
            return true;
          }
        }
      }
      return false;
    },

    cleanup: () => {
      // Disconnect all WebSocket connections
      const { realTimeConnection } = get();
      if (realTimeConnection.websocket) {
        realTimeConnection.websocket.close();
      }

      // Clear all state
      set({
        executions: new Map(),
        activeOperations: new Map(),
        optimisticUpdates: new Map(),
        realTimeConnection: defaultRealTimeConnection(),
        isLoading: false,
        loadingOperations: new Set(),
        globalError: null,
        operationErrors: new Map(),
        pollers: new Map(),
      });
    },

    // Enhanced Polling Actions
    startPolling: async (workflowId: string, runInstanceId: string) => {
      console.log(`[DEBUG] startPolling called with workflowId=${workflowId}, runInstanceId=${runInstanceId}`);

      const executions = new Map(get().executions);
      const execution = executions.get(workflowId);
      if (!execution) {
        console.error('[DEBUG] startPolling: No execution found for orchestration:', workflowId);
        console.log('[DEBUG] startPolling: Available executions:', Array.from(get().executions.keys()));
        return;
      }

      // Check if there's actually a run instance to poll for
      if (!execution.runInstance) {
        console.log('[DEBUG] startPolling: No run instance found, skipping polling');
        return;
      }

      // Do NOT start polling if workflow is stopped, completed, or failed
      if (execution.status === 'stopped' || execution.status === 'completed' || execution.status === 'failed') {
        console.log(`[DEBUG] startPolling: Skipping polling for workflow with status '${execution.status}'`);
        return;
      }

      console.log('[DEBUG] startPolling: Found execution:', {
        runInstance: execution.runInstance,
        status: execution.status,
        isPolling: execution.isPolling,
        runResultsCount: execution.runResults.length
      });
      
      const updatedExecution = {
        ...execution,
        isPolling: true,
        pollingStartTime: Date.now(),
        lastPollTime: Date.now(),
        pollErrorCount: 0,
        error: null,
      };
      executions.set(workflowId, updatedExecution);
      set({ executions });
      
      console.log('[DEBUG] startPolling: Updated execution state, now calling apiService.pollRunResults');
      
      try {
        const apiService = (await import('../services/api')).default;
        console.log('[DEBUG] startPolling: About to call apiService.pollRunResults');
        
        const poller = await apiService.pollRunResults(runInstanceId, {
          onNewResult: (result: RunResult) => {
            console.log('[DEBUG] startPolling: onNewResult callback triggered:', result);
            get().addRunResult(workflowId, result);
            
            // If this is an assistant step result, trigger chat interface update
            if (result.step_type === 'run_agent' && result.final_output) {
              console.log('[DEBUG] startPolling: Assistant step completed, chat interface should update');
              // The chat interface will automatically reload conversation history
              // when the runResults change due to the useEffect dependency
            }
          },
          onStatusChange: (status: RunStatus) => {
            console.log('[DEBUG] startPolling: onStatusChange callback triggered:', status);
            const execs = new Map(get().executions);
            const exec = execs.get(workflowId);
            if (exec) {
              exec.status = status;
              exec.isRunning = status === 'running';
              exec.isPaused = status === 'paused';
              exec.lastUpdated = Date.now();
              execs.set(workflowId, exec);
              set({ executions: execs });

              // If status changed to stopped, completed, or failed, stop polling and clear user input
              if (status === 'stopped' || status === 'completed' || status === 'failed') {
                console.log(`[DEBUG] startPolling: Status changed to '${status}', stopping polling and clearing user input`);
                get().stopPolling(workflowId);
                get().clearUserInputRequired(workflowId);
              }

              // If status is paused, check if we need user input
              if (status === 'paused') {
                // Check if there's a user_prompt step waiting for input
                const userPromptResult = exec.runResults?.find(result =>
                  result.step_type === 'user_prompt' && !result.final_output
                );

                if (userPromptResult) {
                  const currentExec = get().executions.get(workflowId);

                  // Check if this is the same run_result_id we're already waiting for
                  const isSamePrompt = currentExec?.userInputRunResultId === userPromptResult.run_result_id.toString();

                  // If we're already waiting for this exact prompt, skip re-detection
                  if (isSamePrompt && currentExec?.waitingForUserInput === true) {
                    console.log('[DEBUG] startPolling: Skipping user_prompt re-detection in onStatusChange - already waiting for this run_result_id:', userPromptResult.run_result_id);
                  }
                  // If we just cleared this prompt (waitingForUserInput=false but runResultId still matches), skip re-detection
                  else if (isSamePrompt && currentExec?.waitingForUserInput === false) {
                    console.log('[DEBUG] startPolling: Skipping user_prompt re-detection in onStatusChange - input was just submitted for this run_result_id:', userPromptResult.run_result_id);
                  }
                  // New prompt or first time seeing a prompt - show it
                  else {
                    console.log('[DEBUG] startPolling: Found user_prompt step waiting for input:', userPromptResult.run_result_id);
                    get().setUserInputRequired(
                      workflowId,
                      userPromptResult.run_result_id.toString(),
                      userPromptResult.input_message || 'Please provide input'
                    );
                  }
                }
              }
            }
          },
          onComplete: async (response) => {
            console.log('[DEBUG] startPolling: onComplete callback triggered with response');
            console.log('[DEBUG] startPolling: Results count:', response.results.length);
            console.log('[DEBUG] startPolling: Session messages count:', response.session_messages?.length || 0);
            console.log('[DEBUG] startPolling: Session info map keys:', Object.keys(response.session_info_map || {}).length);

            // Refresh the run instance to get the auto-summary (AC-1161)
            let refreshedRunInstance = null;
            try {
              refreshedRunInstance = await apiService.getRunInstance(runInstanceId);
            } catch { /* ignore */ }

            // Update results and session data
            const executions = new Map(get().executions);
            const exec = executions.get(workflowId);
            if (exec) {
              const completedResults = response.results.filter(r => r.status === 'completed');
              const updatedExec = {
                ...exec,
                runResults: response.results,
                ...(refreshedRunInstance ? { runInstance: refreshedRunInstance } : {}),
                currentStep: completedResults.length,
                totalSteps: response.results.length,
                progress: response.results.length > 0 ? (completedResults.length / response.results.length) * 100 : 0,
                lastUpdated: Date.now(),
                stepStatuses: new Map(response.results.map(r => [r.orchestration_step_id, r.status])),
                stepErrors: new Map(response.results.filter(r => r.error_message).map(r => [r.orchestration_step_id, r.error_message!])),
                sessionMessages: response.session_messages || [],
                sessionInfoMap: response.session_info_map || {},
              };
              executions.set(workflowId, updatedExec);
              set({ executions });
              console.log('[DEBUG] startPolling: Updated execution with session data');

              // Check if there's a user_prompt step waiting for input (status will be paused)
              if (updatedExec.status === 'paused') {
                const userPromptResult = response.results.find(result =>
                  result.step_type === 'user_prompt' && !result.final_output
                );

                if (userPromptResult) {
                  const currentExec = get().executions.get(workflowId);

                  // Check if this is the same run_result_id we're already waiting for
                  const isSamePrompt = currentExec?.userInputRunResultId === userPromptResult.run_result_id.toString();

                  // If we're already waiting for this exact prompt, skip re-detection
                  if (isSamePrompt && currentExec?.waitingForUserInput === true) {
                    console.log('[DEBUG] startPolling: Skipping user_prompt re-detection in onComplete - already waiting for this run_result_id:', userPromptResult.run_result_id);
                  }
                  // If we just cleared this prompt (waitingForUserInput=false but runResultId still matches), skip re-detection
                  else if (isSamePrompt && currentExec?.waitingForUserInput === false) {
                    console.log('[DEBUG] startPolling: Skipping user_prompt re-detection in onComplete - input was just submitted for this run_result_id:', userPromptResult.run_result_id);
                  }
                  // New prompt or first time seeing a prompt - show it
                  else {
                    console.log('[DEBUG] startPolling: Found user_prompt step waiting for input:', userPromptResult.run_result_id);
                    get().setUserInputRequired(
                      workflowId,
                      userPromptResult.run_result_id.toString(),
                      userPromptResult.input_message || 'Please provide input'
                    );
                  }
                }
              }
            }

            get().stopPolling(workflowId);
          },
          onError: (error: string) => {
            console.error('[DEBUG] startPolling: onError callback triggered:', error);
            const execs = new Map(get().executions);
            const exec = execs.get(workflowId);
            if (exec) {
              exec.pollErrorCount++;
              exec.error = error;
              exec.lastUpdated = Date.now();
              execs.set(workflowId, exec);
              set({ executions: execs });
              
              // Log detailed error information
              console.error('[DEBUG] Workflow polling error details:', {
                workflowId,
                runInstanceId,
                error,
                pollErrorCount: exec.pollErrorCount,
                timestamp: new Date().toISOString()
              });
            }
          },
          pollingInterval: 3000, // Increased from 1000ms to 3000ms for better performance
          maxDuration: 300000, // 5 minutes
          enableProgressiveUpdates: true,
        });
        
        console.log('[DEBUG] startPolling: poller created successfully, storing in pollers map');
        const pollers: Map<string, { stop: () => void }> = new Map(get().pollers || new Map());
        pollers.set(workflowId, poller);
        set({ pollers });
        console.log('[DEBUG] startPolling: Polling started successfully for workflowId:', workflowId);
        
      } catch (error) {
        console.error('[DEBUG] startPolling: Failed to start polling:', error);
        const execs = new Map(get().executions);
        const exec = execs.get(workflowId);
        if (exec) {
          exec.isPolling = false;
          exec.error = error instanceof Error ? error.message : 'Failed to start polling';
          execs.set(workflowId, exec);
          set({ executions: execs });
        }
      }
    },

    stopPolling: (workflowId: string) => {
      console.log('[DEBUG] stopPolling called for workflowId:', workflowId);

      // Stop the poller
      const pollers: Map<string, { stop: () => void }> = new Map(get().pollers || new Map());
      const poller = pollers.get(workflowId);
      if (poller) {
        console.log('[DEBUG] stopPolling: Stopping poller for workflowId:', workflowId);
        poller.stop();
        pollers.delete(workflowId);
        set({ pollers });
      }

      // Update execution state only if execution exists
      const executions = new Map(get().executions);
      const execution = executions.get(workflowId);
      if (execution) {
        const updatedExecution = {
          ...execution,
          isPolling: false,
          lastPollTime: Date.now(),
        };
        executions.set(workflowId, updatedExecution);
        set({ executions });
        console.log('[DEBUG] stopPolling: Updated execution state for workflowId:', workflowId);

        // AC-1366: if polling was stopped while the local view still thinks
        // the run is in flight (e.g. silent maxDuration timeout, 401, network
        // error), do an authoritative status check via REST. This is what
        // turns the previously stuck-spinner failure mode into a self-healing
        // one - without it, the local isRunning stays true forever.
        const wasNonTerminal =
          updatedExecution.isRunning ||
          updatedExecution.isPaused ||
          (updatedExecution.status !== 'completed' &&
           updatedExecution.status !== 'failed' &&
           updatedExecution.status !== 'stopped');
        const runInstanceId = updatedExecution.runInstance?.run_instance_id;
        if (wasNonTerminal && runInstanceId) {
          (async () => {
            try {
              const apiService = (await import('../services/api')).default;
              const fresh = await apiService.getRunInstance(String(runInstanceId));
              const freshStatus = fresh.status as RunStatus;
              const freshExecutions = new Map(get().executions);
              const freshExec = freshExecutions.get(workflowId);
              if (!freshExec) return;
              freshExecutions.set(workflowId, {
                ...freshExec,
                runInstance: fresh,
                status: freshStatus,
                isRunning: freshStatus === 'running',
                isPaused: freshStatus === 'paused',
                lastUpdated: Date.now(),
              });
              set({ executions: freshExecutions });
            } catch (err) {
              console.warn('[DEBUG] stopPolling: post-stop reconcile failed', err);
            }
          })();
        }
      } else {
        console.log('[DEBUG] stopPolling: No execution found for workflowId:', workflowId);
      }
    },

    updateRunResults: (workflowId: string, results: RunResult[]) => {
      console.log('[DEBUG] updateRunResults called with workflowId:', workflowId, 'results count:', results.length);
      console.log('[DEBUG] updateRunResults: Full results:', results);
      
      const executions = new Map(get().executions);
      const execution = executions.get(workflowId);
      if (execution) {
        const completedResults = results.filter(r => r.status === 'completed');
        const updatedExecution = {
          ...execution,
          runResults: results,
          currentStep: completedResults.length,
          totalSteps: results.length,
          progress: results.length > 0 ? (completedResults.length / results.length) * 100 : 0,
          lastUpdated: Date.now(),
          stepStatuses: new Map(results.map(r => [r.orchestration_step_id, r.status])),
          stepErrors: new Map(results.filter(r => r.error_message).map(r => [r.orchestration_step_id, r.error_message!])),
        };
        executions.set(workflowId, updatedExecution);
        set({ executions });
        console.log('[DEBUG] updateRunResults: Store updated successfully with', results.length, 'results');
      } else {
        console.error('[DEBUG] updateRunResults: No execution found for workflowId:', workflowId);
      }
    },

    addRunResult: (workflowId: string, result: RunResult) => {
      console.log('[DEBUG] addRunResult called with workflowId:', workflowId, 'result:', result);
      
      const executions = new Map(get().executions);
      const execution = executions.get(workflowId);
      if (execution) {
        const existingIndex = execution.runResults.findIndex(r => r.run_result_id === result.run_result_id);
        let updatedResults: RunResult[];
        if (existingIndex >= 0) {
          console.log('[DEBUG] addRunResult: Updating existing result at index:', existingIndex);
          updatedResults = [...execution.runResults];
          updatedResults[existingIndex] = result;
        } else {
          console.log('[DEBUG] addRunResult: Adding new result to results array');
          updatedResults = [...execution.runResults, result];
        }
        const completedResults = updatedResults.filter(r => r.status === 'completed');
        const updatedExecution = {
          ...execution,
          runResults: updatedResults,
          currentStep: completedResults.length,
          totalSteps: updatedResults.length,
          progress: updatedResults.length > 0 ? (completedResults.length / updatedResults.length) * 100 : 0,
          lastUpdated: Date.now(),
          stepStatuses: new Map(updatedResults.map(r => [r.orchestration_step_id, r.status])),
          stepErrors: new Map(updatedResults.filter(r => r.error_message).map(r => [r.orchestration_step_id, r.error_message!])),
        };
        executions.set(workflowId, updatedExecution);
        set({ executions });
        console.log('[DEBUG] addRunResult: Store updated successfully. Total results now:', updatedResults.length);
      } else {
        console.error('[DEBUG] addRunResult: No execution found for workflowId:', workflowId);
      }
    },

    setUserInputRequired: (workflowId: string, runResultId: string, message: string) => {
      const executions = new Map(get().executions);
      const execution = executions.get(workflowId);
      if (execution) {
        const updatedExecution = {
          ...execution,
          waitingForUserInput: true,
          userInputRunResultId: runResultId,
          userInputMessage: message,
          lastUpdated: Date.now(),
        };
        executions.set(workflowId, updatedExecution);
        set({ executions });
        
        // Stop polling when waiting for user input to prevent unnecessary API calls
        get().stopPolling(workflowId);
      }
    },

    clearUserInputRequired: (workflowId: string) => {
      const executions = new Map(get().executions);
      const execution = executions.get(workflowId);
      if (execution) {
        const updatedExecution = {
          ...execution,
          waitingForUserInput: false,
          // Keep userInputRunResultId so we can track which prompt was just cleared
          // This prevents re-detection of the same prompt after submission
          // userInputRunResultId: null,  // DON'T clear this
          userInputMessage: null,
          lastUpdated: Date.now(),
        };
        executions.set(workflowId, updatedExecution);
        set({ executions });

        // Resume polling after user input is provided, but NOT if workflow is in terminal state
        // Terminal states: completed, failed, stopped
        const isTerminal = execution.status === 'completed' || execution.status === 'failed' || execution.status === 'stopped';
        if (execution.runInstance && !isTerminal) {
          get().startPolling(workflowId, execution.runInstance.run_instance_id);
        }
      }
    },
  }))
);

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    useWorkflowStore.getState().cleanup();
  });
}